import { Stack, Box, Typography, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  ICandidate,
  submitCandidate,
  UpdateCandidateDetails,
  updateCandidateDetails,
} from 'wisestep-api';
import {
  WsTypoVariants,
  WsTypoColors,
  SelectEdit,
  NumberEdit,
  DateEdit,
  LocationSearchEdit,
  TextEdit,
  IUserDTO,
} from 'wisestep-base';

import { useUserContext } from '../../../../../context';
import { success, warning } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { useCandidateViewData } from '../../../utils/CandidateViewContext';

import {
  commonOptions,
  communicationOptions,
  genderOptions,
  jobPreferrenceType,
  jobTypeOptions,
  noticePeriodOptions,
  visaOptions,
} from './Helper';

interface TextWithLabelProps {
  value: any;
  label: string;
  labelVariant?: WsTypoVariants;
  valueVariant?: WsTypoVariants;
  ml?: number;
  labelColor?: string;
  valueColor?: WsTypoColors;
  candidate?: ICandidate;
  isProfile?: boolean;
}
const TextWithLabel = ({
  value,
  label,
  labelColor = WsTypoColors.SECONDARY,
  labelVariant,
  ml = 4,
  candidate,
  isProfile,
}: TextWithLabelProps) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const methods = useForm();
  const { updateCandidateData } = useCandidateViewData();

  const fieldValues = () => {
    const componentMap = {
      Gender: (
        <SelectEdit
          name={label}
          selectArray={genderOptions}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
        />
      ),
      'Visa Status': (
        <SelectEdit
          name={label}
          selectArray={visaOptions}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
          disabled={true}
        />
      ),
      'Willing to Relocate': (
        <SelectEdit
          name={label}
          selectArray={commonOptions}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
        />
      ),
      'Willing to Travel': (
        <SelectEdit
          name={label}
          selectArray={commonOptions}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
        />
      ),
      'Latest Job Includes Travel': (
        <SelectEdit
          name={label}
          selectArray={commonOptions}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
        />
      ),
      'Family willing to relocate': (
        <SelectEdit
          name={label}
          selectArray={commonOptions}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
        />
      ),
      'Interviewing With Others': (
        <SelectEdit
          name={label}
          selectArray={commonOptions}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
        />
      ),
      'Communication Preference': (
        <SelectEdit
          name={label}
          selectArray={communicationOptions}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
        />
      ),
      'Total experience': (
        <NumberEdit
          name={label}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={Number(value)}
        />
      ),
      'Relevant experience': (
        <NumberEdit
          name={label}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={Number(value)}
        />
      ),
      'Current Salary': (
        <NumberEdit
          name={label}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={value}
          disable={true}
        />
      ),
      'Expected Salary': (
        <NumberEdit
          name={label}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={value ?? 'NA'}
          disable={true}
        />
      ),
      'Latest Job Type': (
        <SelectEdit
          name={label}
          selectArray={jobTypeOptions}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
        />
      ),
      'Job Preference': (
        <SelectEdit
          name={label}
          selectArray={jobPreferrenceType}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
        />
      ),
      'Notice Period': (
        <SelectEdit
          name={label}
          selectArray={noticePeriodOptions}
          watch={methods.watch}
          onConfirm={updateDetails}
          defaultValue={String(value)}
          color={WsTypoColors.TERTIARY900}
        />
      ),
      'Visa Validity': (
        <DateEdit
          fieldName={label}
          watch={methods.watch}
          defaultValue={value?.toString()}
          onConfirm={updateDetails}
          disabled={true}
        />
      ),
      'Date of Birth': (
        <DateEdit
          fieldName={label}
          watch={methods.watch}
          defaultValue={value}
          onConfirm={updateDetails}
        />
      ),
      'Available From': (
        <DateEdit
          fieldName={label}
          watch={methods.watch}
          defaultValue={value?.toString()}
          onConfirm={updateDetails}
        />
      ),
      // Location: (
      //   <LocationSearchEdit
      //     onConfirm={updateDetails}
      //     watch={methods.watch}
      //     name={label}
      //     defaultValue={String(value)}
      //     multiSelect={false}
      //   />
      // ),
      'Current Location': (
        <LocationSearchEdit
          name={label}
          watch={methods.watch}
          defaultValue={value}
          onConfirm={updateDetails}
          multiSelect={false}
        />
      ),
    };
    const component = componentMap[label];
    return (
      <Tooltip title={value}>
        {component ? (
          component
        ) : (
          <TextEdit
            fieldName={label}
            defaultValue={String(value)}
            onConfirm={updateDetails}
            disabled={true}
          />
        )}
      </Tooltip>
    );
  };
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [showTooltip, setShowTooltip] = useState(true);

  const updateDetails = async (field, value) => {
    let updatedField = field;
    const updatedCandidate = {
      ...candidate,
    };

    if (field == 'Gender') {
      updatedField = 'gender';
      updatedCandidate.gender = value;
    } else if (field == 'Visa Status') {
      updatedField = 'visaStatus';
      updatedCandidate.visaStatus = value;
    } else if (field == 'Nationality') {
      updatedField = 'nationality';
      updatedCandidate.nationality = value;
    } else if (field == 'Willing to Relocate') {
      updatedField = 'willingToRelocate';
      updatedCandidate.willingToRelocate = value == 'YES' ? true : false;
    } else if (field == 'Latest Job Includes Travel') {
      updatedField = 'jobIncludesTravel';
      updatedCandidate.jobIncludesTravel = value == 'YES' ? true : false;
    } else if (field == 'Family willing to relocate') {
      updatedField = 'familyWillingToRelocate';
      updatedCandidate.familyWillingToRelocate = value == 'YES' ? true : false;
    } else if (field == 'Interviewing With Others') {
      updatedField = 'interviewingWithOthers';
      updatedCandidate.interviewingWithOthers = value == 'YES' ? true : false;
    } else if (field == 'Communication Preference') {
      updatedField = 'commPreferencePhone';
      if (value == 'Email') {
        updatedCandidate.commPreferenceEmail = true;
      } else if (value == 'Phone') {
        updatedCandidate.commPreferencePhone = true;
      } else if (value == 'Email & Phone') {
        updatedCandidate.commPreferenceEmail = true;
        updatedCandidate.commPreferencePhone = true;
      }
    } else if (field == 'Source') {
      updatedField = 'sources';
      updatedCandidate.source = value;
    } else if (field == 'Total experience') {
      updatedField = 'totalYearsExperience';
      updatedCandidate.totalYearsExperience = value;
    } else if (field == 'Relevant experience') {
      updatedField = 'relevantYearsExperience';
      updatedCandidate.relevantYearsExperience = value;
    } else if (field == 'Current Salary') {
      updatedField = 'currentSalary';
      updatedCandidate.currentSalary = value;
    } else if (field == 'Expected Salary') {
      updatedField = 'expectedBaseSalary';
      updatedCandidate.expectedBaseSalary = value;
    } else if (field == 'Latest Job Type') {
      updatedField = 'currentJobType';
      updatedCandidate.currentJobType = value;
    } else if (field == 'Visa Validity') {
      updatedField = 'visaValidity';
      updatedCandidate.visaValidity = value;
    } else if (field == 'Authorizations') {
      updatedField = 'workAuthorization';
      updatedCandidate.workAuthorization = value;
    } else if (field == 'Latest Employer') {
      updatedField = 'currentEmployer';
      updatedCandidate.currentEmployer = value;
    } else if (field == 'Latest Job Title') {
      updatedField = 'currentJobTitle';
      updatedCandidate.currentJobTitle = value;
    } else if (field == 'Willing to Travel') {
      updatedField = 'willingToTravel';
      updatedCandidate.willingToTravel = value == 'YES' ? true : false;
    } else if (field === 'Profile Urls') {
      updatedField = 'profileURLs';
      updatedCandidate.profileURLs = value.split(',');
    } else if (field === 'Institutions Attended') {
      updatedField = 'institutionsAttended';
      updatedCandidate.institutionsAttended = value.split(',');
    } else if (field === 'Previous Job Titles') {
      updatedField = 'previousJobTitles';
      updatedCandidate.previousJobTitles = value.split(',');
    } else if (field === 'Previous Employers') {
      updatedField = 'previousEmployers';
      updatedCandidate.previousEmployers = value.split(',');
    } else if (field === 'Job Preference') {
      updatedField = 'preferredJobTypes';
      if (value == 'Contract') {
        updatedCandidate.jobPreferenceContract = true;
      } else if (value == 'Permanent') {
        updatedCandidate.jobPreferencePermanent = true;
      } else if (value == 'Contract & Permanent') {
        updatedCandidate.jobPreferenceContract = true;
        updatedCandidate.jobPreferencePermanent = true;
      }
      updatedCandidate.preferredJobTypes = [value];
    } else if (field === 'Tags') {
      updatedField = 'tags';
      updatedCandidate.tags = value.split(',');
    } else if (field === 'Date of Birth') {
      updatedField = 'DOB';
      updatedCandidate.dob = value;
    } else if (field === 'Current Location') {
      updatedField = 'currentLocation';
      updatedCandidate.currentLocation = value;
    } else if (field === 'Other allowances and benefits') {
      updatedField = 'otherCurrentAllowances';
      updatedCandidate.otherCurrentAllowances = value;
    } else if (field === 'Expected allowances and benefits') {
      updatedField = 'otherExpectedAllowances';
      updatedCandidate.otherExpectedAllowances = value;
    } else if (field === 'Passport Number') {
      updatedField = 'passportNumber';
      updatedCandidate.passportNumber = value;
    } else if (field === 'Passport Type') {
      updatedField = 'passportType';
      updatedCandidate.passportType = value;
    } else if (field === 'SSN Number') {
      updatedField = 'ssnNumber';
      updatedCandidate.ssnNumber = value;
    } else if (field === 'Aadhar Number') {
      updatedField = 'aadharNumber';
      updatedCandidate.aadharNumber = value;
    } else if (field === 'PAN Card') {
      updatedField = 'panCard';
      updatedCandidate.panCard = value;
    } else if (field === 'Reason for looking for a new job') {
      updatedField = 'reasonForNewJob';
      updatedCandidate.reasonForNewJob = value;
    } else if (field === 'Notice Period') {
      updatedField = 'noticePeriod';
      updatedCandidate.noticePeriod = value;
    } else if (field === 'Recruiter Assessment') {
      updatedField = 'assessment';
      updatedCandidate.assessment = value;
    } else if (field === 'Highest education qualifications') {
      updatedField = 'educationQualifications';
      updatedCandidate.educationQualifications = value.split(',');
    }
    const candidateDetails: UpdateCandidateDetails = {
      candidate: updatedCandidate as ICandidate,
      isSingleEdit: true,
      field: updatedField,
    };

    try {
      if (isProfile) {
        candidateDetails.candidate.recruiterId = loggedInUser.email ?? '';
        await updateCandidateDetails(candidateDetails);
      } else {
        await submitCandidate(updatedCandidate as ICandidate);
      }
      setNotification(success(`Candidate ${field} updated Sucessfully`));
      updateCandidateData && updateCandidateData();
    } catch {
      setNotification(warning('Something went wrong, Please try again.'));
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack direction="column" ml={ml} rowGap={0.5}>
        <Tooltip title={value}>
          <Typography
            variant={labelVariant}
            color={labelColor}
            sx={{
              fontSize: 12,
              overflow: 'hidden',
              display: 'block',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {label}
          </Typography>
        </Tooltip>
        {typeof value !== 'string' && typeof value !== 'number' ? (
          <Tooltip
            title={Array.isArray(value) ? value.join(', ') : value}
            disableHoverListener={!showTooltip}
          >
            <Box
              sx={{
                fontSize: 14,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                width: 100,
                cursor: 'pointer',
              }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(true)}
              onClick={() => setShowTooltip(false)}
            >
              {Array.isArray(value) ? value.join(', ') : value}
            </Box>
          </Tooltip>
        ) : (
          <>{fieldValues()}</>
        )}
      </Stack>
    </FormProvider>
  );
};

export default TextWithLabel;
